import {
    memo,
    useState,
    useEffect,
    useCallback,
    useRef,
    forwardRef,
    useImperativeHandle,
    useMemo,
    type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { noop, isNil, first, isEmpty } from 'lodash';
// Import Types
import type { UseListFiltersReturnValue } from 'hooks/useListFilters';
// Import Store
// Import UI Components
import { Switcher, Tooltip } from '@geneui/components';
import DataTable, { type DataTableRefActions } from 'components/common/DataTable';
import DownloadOrSendToEmailToolbar from 'components/common/DownloadOrSendToEmailToolbar';
import CommunicationActionsModals from './CommunicationActionsModals';
import CommunicationActionsButtons from './CommunicationActionsButtons';
// Import Hooks
import useToaster from 'hooks/UseToaster';
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useQueryParams from 'hooks/UseQueryParams';
import { useGTMEvent } from 'hooks/UseGTM';
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
// Import Services
import { CommunicationHttpService } from 'services/http';
// Import Enums
import { DataTableConfigKeysEnum } from 'enums';
// Import Constants
import { GACategoryEvents, ModalsActions, l } from 'constants/common';
import { DataTableColumnsConfigs, CommunicationPagingOptionsShowAll } from './config';
// Import Styles
import 'assets/scss/segmentListPage.scss';

const { GAFiltersCategory } = GACategoryEvents;

interface Props {
    eventKey: string;
    tableKey?: string;
    campaignId?: string;
    getHeaderActions?: () => void;
    isShowVerticalDots?: boolean;
    isShowColumnChooser?: boolean;
    shouldShowPromotionId?: boolean;
    shouldShowExecutionId?: boolean;
    shouldExecutionIdBePermanent?: boolean;
    shouldSendFileNameForDownload?: boolean;
    isHasLeftHeaderActions?: boolean;
    headerLeftActions?: ReactNode;
    listFilters: UseListFiltersReturnValue;
}

export interface CommunicationListRef {
    reset: () => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Filters?: any;
}

const CommunicationList = forwardRef<CommunicationListRef, Props>(
    (
        {
            tableKey = DataTableConfigKeysEnum.dataTableCommunication,
            campaignId,
            getHeaderActions = noop,
            isShowVerticalDots = true,
            isShowColumnChooser = true,
            shouldShowPromotionId,
            shouldShowExecutionId,
            shouldExecutionIdBePermanent = true,
            shouldSendFileNameForDownload,
            isHasLeftHeaderActions,
            headerLeftActions,
            eventKey,
            listFilters,
        },
        ref,
    ) => {
        const { t } = useTranslation();
        const { showToaster } = useToaster();
        const { doPostRequest } = useRequest();
        const { logEvent } = useGTMEvent();
        const dateFormats = usePartnerDateFormats();

        const dataTableRef = useRef<DataTableRefActions>(null);

        const timeoutId = useRef(-1);

        const communicationConfigurationQueryParams = useQueryParams('communicationConfiguration');

        const [isShowAllMode, setIsShowAllMode] = useState<boolean>(() => {
            const decodedValue = communicationConfigurationQueryParams.decode();
            return !isEmpty(decodedValue) && !!decodedValue?.showAll;
        });

        const { isMounted, baseRequestBody, changePageNumber, changePageSize, changeSorting } = listFilters;

        const [isCommunicationListLoading, setIsCommunicationListLoading] = useState(false);
        const [communicationListData, setCommunicationListData] = useState([]);
        const [communicationListDataCount, setCommunicationListDataCount] = useState(0);

        const [isResendModalOpenedState, setIsResendModalOpenedState] = useState(false);
        const [isFileLoading, setIsFileLoading] = useState(false);

        const [openedModalData, setOpenedModalData] = useState<AnyObject>({});

        const { columns } = useMemo(
            () =>
                DataTableColumnsConfigs({
                    t,
                    shouldShowPromotionId,
                    shouldShowExecutionId,
                    shouldExecutionIdBePermanent,
                    dateFormats,
                }),
            [dateFormats, shouldExecutionIdBePermanent, shouldShowExecutionId, shouldShowPromotionId, t],
        );

        // Communication fetch part
        const { getCommunicationsRequest } = useCancelableRequests(
            {
                getCommunicationsRequest: CommunicationHttpService.getCommunicationList,
            },
            { takeLatest: ['getCommunicationsRequest'] },
        );

        // kept out of the hook: a file download must not be aborted when the user leaves the page
        const { downloadCommunicationsReportRequest } = useMemo(
            () => ({
                // eslint-disable-next-line no-restricted-syntax -- download must survive unmount
                downloadCommunicationsReportRequest: CommunicationHttpService.downloadCommunicationsReport(),
            }),
            [],
        );

        const getCommunications = useCallback(
            (filter = baseRequestBody) => {
                setIsCommunicationListLoading(true);
                doPostRequest(getCommunicationsRequest.request, {
                    requestBody: filter,
                    queryString: { withTotalCount: isShowAllMode },
                    successCallback: ({ Data, Count }) => {
                        setCommunicationListData(getTableRows(Data));
                        setCommunicationListDataCount(Count);
                    },
                }).then(({ IsCanceled }) => {
                    // A superseded call must not hide the loader of the call that replaced it
                    if (!IsCanceled) {
                        setIsCommunicationListLoading(false);
                    }
                });
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [baseRequestBody, isShowAllMode],
        );

        const resendCommunicationAction = (communication) => {
            setOpenedModalData(communication);
            setIsResendModalOpenedState(true);
        };

        const modalsStateSetDict = {
            resendCommunication: {
                key: 'resendCommunication',
                fn: setIsResendModalOpenedState,
            },
        };

        const getTableRefreshFn = () => {
            getCommunications(baseRequestBody);
        };

        const onCloseModalHandler = (modalStateKey, action, alertType, alertMessage, _count = 1) => {
            if (action === ModalsActions.RESEND) {
                showToaster(alertType, alertMessage);
                getTableRefreshFn();
            }

            modalsStateSetDict[modalStateKey].fn(false);
        };

        const getRowActionBar = (row) => (
            <CommunicationActionsButtons data={row.data} resendCommunicationAction={resendCommunicationAction} />
        );

        const getTableRows = (communicationList) => {
            return communicationList.map(
                ({
                    CommunicationId,
                    CampaignExecuteResultId,
                    ClientId,
                    ClientName,
                    SentDate,
                    Channel,
                    Status,
                    Message,
                    ParentId,
                    PromotionId,
                    ObjectId,
                    CanResend,
                    ObjectName,
                }) => {
                    return {
                        hasHover: true,
                        dragDisable: true,
                        nestedData: [],
                        data: {
                            rowKey: CommunicationId,
                            isRowOpend: false,
                            CommunicationId,
                            ClientId,
                            ClientName,
                            SentDate,
                            Channel,
                            Status,
                            Message,
                            ParentId,
                            PromotionId,
                            ObjectId,
                            CanResend,
                            ObjectName,
                            ...(shouldShowExecutionId ? { ExecutionId: CampaignExecuteResultId } : {}),
                        },
                    };
                },
            );
        };

        const getDataTableName = () => <span>{t(l.Communications)}</span>;

        useImperativeHandle(ref, () => ({
            reset: () => {
                dataTableRef.current?.reset?.();
            },
        }));

        const dynamicProps = useMemo(() => {
            return !isShowAllMode
                ? { receivedCurrentPageSize: first(CommunicationPagingOptionsShowAll[`${isShowAllMode}`])?.value }
                : {};
        }, [isShowAllMode]);

        const showAllModeChangeHandler = () => {
            logEvent(
                GAFiltersCategory.name,
                GAFiltersCategory.events[`${eventKey}All`],
                GAFiltersCategory.labels.communicationListAll,
                1,
            );
            setIsShowAllMode((prev) => {
                if (!isNil(dataTableRef?.current)) {
                    dataTableRef.current?.changeTableConfig?.({
                        pageNumber: 1,
                        pageSize: first(CommunicationPagingOptionsShowAll[`${!prev}`])?.value,
                    });
                }
                communicationConfigurationQueryParams.historyReplace({ showAll: !prev });
                return !prev;
            });
        };

        // Filter update part
        useEffect(() => {
            if (isMounted.current) {
                getCommunications(baseRequestBody);
            }
            /* eslint-disable react-hooks/exhaustive-deps */
        }, [baseRequestBody]);

        const cleanUp = () => {
            return () => {
                if (timeoutId.current !== -1) {
                    clearInterval(timeoutId.current);
                }
            };
        };

        const getRequestBody = () => {
            return {
                Filters: baseRequestBody.Filters,
                ...(!isShowAllMode && { Pageing: baseRequestBody.Pageing }),
            };
        };

        const downloadAllOrSendEmailClickHandler = (isDownload) => {
            setIsFileLoading(true);
            return doPostRequest(downloadCommunicationsReportRequest.request, {
                requestBody: getRequestBody(),
                queryString: {
                    isDownload,
                    ...(shouldSendFileNameForDownload ? { fileName: `Communications Campaign (${campaignId})` } : null),
                },
                successCallback: (data) => {
                    isDownload && window.open(data?.Url);
                },
                isShowError: false,
            }).then(({ AlertType, AlertMessage, IsCanceled }) => {
                // Aborted on unmount: no toaster
                if (IsCanceled) return;
                showToaster(AlertType, AlertMessage);
                setIsFileLoading(false);
            });
        };

        const headerActions = () => {
            return (
                <>
                    <div className="table-header-filter-switcher-container">
                        {getHeaderActions()}
                        <Switcher
                            label={
                                <Tooltip text={t(l.ShowAllTooltipInfo)} position="bottom">
                                    <span>{t(l.ShowAll)}</span>
                                </Tooltip>
                            }
                            size="big"
                            checked={isShowAllMode}
                            onChange={showAllModeChangeHandler}
                        />
                    </div>
                </>
            );
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
        useEffect(cleanUp, []);

        return (
            <>
                <DataTable
                    rowKey="CommunicationId"
                    columnKey="dataKey"
                    name={getDataTableName()}
                    isColumnsSortable={true}
                    tableKey={tableKey}
                    withPagination={true}
                    setLabelObjectIdKey="CommunicationId"
                    labelNameKey="CategoryName"
                    labelColorKey="CategoryColor"
                    data={communicationListData}
                    dataCount={communicationListDataCount}
                    rowActionBar={getRowActionBar}
                    columns={columns}
                    isLoading={isCommunicationListLoading}
                    isShowRefreshButton={true}
                    isShowColumnChooser={isShowColumnChooser}
                    isShowVerticalDots={isShowVerticalDots}
                    setPagingPageSize={changePageSize}
                    setPagingPageNumber={changePageNumber}
                    sorting={baseRequestBody.Sorting}
                    setSorting={changeSorting}
                    onRefreshClick={getTableRefreshFn}
                    headerActions={headerActions()}
                    withQueryParams={true}
                    ref={dataTableRef}
                    pagingOptions={CommunicationPagingOptionsShowAll[`${isShowAllMode}`]}
                    TitleActions={
                        <DownloadOrSendToEmailToolbar
                            downloadAction={() => downloadAllOrSendEmailClickHandler(true)}
                            sendToEmailAction={() => downloadAllOrSendEmailClickHandler(false)}
                            isFileLoading={isFileLoading}
                            isDisabledDownload={isNil(communicationListDataCount) || communicationListDataCount === 0}
                            shouldCloseOnAction={true}
                        />
                    }
                    isHasLeftHeaderActions={isHasLeftHeaderActions}
                    headerLeftActions={headerLeftActions}
                    {...dynamicProps}
                />

                <CommunicationActionsModals
                    isResendModalOpenedState={isResendModalOpenedState}
                    onCloseModalHandler={onCloseModalHandler}
                    modalsStateSetDict={modalsStateSetDict}
                    openedModalData={openedModalData}
                />
            </>
        );
    },
);

CommunicationList.displayName = 'CommunicationList';

export default memo(CommunicationList);
